import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useOrpc } from '@/_shared/hooks/useOrpc';

/**
 * Challenge-mode creator attribution, resolved client-side from the workspace member roster.
 *
 * In a challenge workspace, admins/owners see every attendee's artifacts; this hook lets list views
 * label each resource with the name of the member who created it. Resolution is purely client-side
 * (from `workspace.get`'s members list), so no rpc response shape changes to carry a name.
 *
 * `showCreators` is false in every non-challenge workspace and for non-admins, so callers can gate
 * the badge with a single boolean and render nothing otherwise.
 */
export function useChallengeCreators(): {
  showCreators: boolean;
  nameFor: (userId: string | null | undefined) => string | null;
} {
  const orpc = useOrpc();
  const { isAdmin } = useWorkspace();
  const { data } = useQuery(orpc.workspace.get.queryOptions({ input: {} }));

  const nameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const member of data?.members ?? []) {
      const name = member.user.displayName ?? member.user.githubLogin ?? member.user.email ?? 'Unknown';
      map.set(member.userId, name);
    }
    return map;
  }, [data?.members]);

  const showCreators = (data?.challengeMode ?? false) && isAdmin;

  // Memoized: list pages gate `columns`/`renderCard` memos on this, so a fresh closure per render would
  // rebuild the table's column model and re-render every card on each keystroke.
  const nameFor = useCallback(
    (userId: string | null | undefined) => (userId ? (nameById.get(userId) ?? null) : null),
    [nameById],
  );

  return { showCreators, nameFor };
}
